// components/VerifyOtp.tsx

import { useState, useRef } from "react";
import {
  useRequestOtpMutation,
  useVerifyOtpMutation,
} from "../../Services/AuthService";
import { useLocation, useNavigate } from "react-router-dom";
import Config from "../../Config";
import { useDispatch } from "react-redux";
import { setAccessToken, setAuthUser } from "../../Redux/Slices/AuthSlice";
import { setAddAddress } from "../../Redux/Slices/SettingSlice";
import { IoArrowBack } from "react-icons/io5";
import Storage from "./../../Helpers/Storage";
import { useToast } from "../../Helpers/Hooks/useToast";
import TurnstileWidget from "../../Helpers/TurnstileWidget";

interface VerifyProps {
  email?: string;
  condition?: boolean;
  onSuccess?: () => void;
  onRedirect?: () => void;
}

const VerifyOtp: React.FC<VerifyProps> = ({
  email,
  condition,
  onSuccess,
  onRedirect,
}) => {
  const toast = useToast();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [verifyOtpTrigger] = useVerifyOtpMutation();
  const [requestOtpTrigger, { isLoading: resendLoading }] = useRequestOtpMutation();

  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const inputRefs = useRef<HTMLInputElement[]>([]);

  /*
   * Resend calls customer-sendOtptoEmail, which is behind requireCaptcha. This
   * screen is reached straight after sign-up, before the customer has a
   * session, so there is nothing for `allowAuthenticated` to let through and
   * the request needs a token of its own. It had neither a widget nor a token,
   * so Resend was refused with `missing_token` on the first click.
   */
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const refreshCaptcha = () => {
    setCaptchaToken(null);
    setCaptchaResetSignal((n) => n + 1);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return; // Allow only digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getFullOtp = () => otp.join("");

  const emailOrPhoneValue = location.state?.email || "";

  const actualEmail = emailOrPhoneValue || email || "";

  function getFirstAndLastNameFromEmail(email: any) {
    const localPart = email.split("@")[0];
    const parts = localPart.split(/[._-]/); // split on dot, underscore, or hyphen

    const firstName = parts[0]
      ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase()
      : "";

    const lastName = parts[1]
      ? parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase()
      : "";

    return { firstName, lastName };
  }

  const handleResendOTP = async () => {
    try {
      const obj: any = {
        email: actualEmail,
        phoneNumber: "",
        message: "resent",
        captchaToken,
      };

      const result: any = await requestOtpTrigger(obj);

      if (result?.data && result?.data?.message) {
        setError("");
        toast.success(result?.data?.message, 2000);
        setMessage(result?.data?.message);
        setOtp(new Array(6).fill(""));
      } else if (result?.error?.data?.message) {
        /*
         * requestOtpTrigger is not unwrapped, so a rejected request resolves
         * with `error` set instead of throwing -- the catch below never ran.
         * With only the success branch written, a refused resend showed the
         * customer nothing at all: no toast, no message, a button that looked
         * broken. That is how this fault stayed invisible.
         */
        setMessage("");
        setError(result.error.data.message);
        toast.error(result.error.data.message, 3000);
      } else {
        setMessage("");
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong.");
      setMessage("");
    } finally {
      // Single-use token: the one just sent is dead either way.
      refreshCaptcha();
    }
  };

  const handleVerify = async () => {
    const obj: any = {
      email: actualEmail,
      otp: getFullOtp(),
    };

    try {
      const result = await verifyOtpTrigger(obj).unwrap();

      if (result && result?.data && result?.message) {
        toast.success(result?.message, 2000);
        setMessage(result?.message || "Account Activated");
        setError("");

        // Extract values from correct structure
        const userData = result.data;
        const customer = result.customer || {};
        const tokenArray = userData.refreshToken || [];

        // pick the latest token if available
        const token = Array.isArray(tokenArray)
          ? tokenArray[tokenArray.length - 1]
          : "";

        if (token && customer?.userId) {
          const auth: any = {
            accessToken: token,
            email: userData.email,
            firstName:
              userData.firstName?.trim() ||
              getFirstAndLastNameFromEmail(userData.email).firstName,
            lastName:
              userData.lastName?.trim() ||
              getFirstAndLastNameFromEmail(userData.email).lastName,
            status: userData.status,
            _id: customer.userId || "",
            addresses: userData.addresses || [],
          };

          // Store auth locally
          Storage.set(`${Config.STORAGE_KEY}/auth`, JSON.stringify(auth));

          // Update Redux
          dispatch(
            setAccessToken({
              accessToken: auth.accessToken,
              refreshToken: auth.accessToken,
            }),
          );
          dispatch(setAuthUser(auth));

          // Clear any previous addresses
          dispatch(setAddAddress(auth.addresses || []));

          // Navigate home
          navigate("/", { replace: true });
        } else {
          console.warn("No token or customer ID found in response:", result);
          toast.error("Invalid login response. Please try again.", 2000);
        }
      }
    } catch (err: any) {
      console.error("verify error:", err);
      toast.error(err?.data?.message || "Something went wrong.", 2000);
      setError(err?.data?.message || "Something went wrong.");
      setMessage("");
    }
  };

  return (
    <>
      <div
        className={`${
          emailOrPhoneValue === undefined
            ? "min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-background px-4"
            : condition
              ? "flex items-center justify-center"
              : "flex min-h-screen items-center justify-center"
        }`}
      >
        <div className="w-full max-w-md border border-gray-300 dark:border-dark-foreground rounded-lg shadow-md p-8 bg-white dark:bg-dark-foreground">
          {!condition ? (
            <button
              onClick={() => navigate("/auth/signin")}
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
            >
              <IoArrowBack className="text-lg sm:text-xl" />
              <span>Back to Login</span>
            </button>
          ) : (
            <button
              onClick={onRedirect}
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
            >
              <IoArrowBack className="text-lg sm:text-xl" />
              <span>Back</span>
            </button>
          )}

          <h2 className="text-xl font-semibold text-center mb-4 mt-5 dark:text-white">
            Verify Email Address
          </h2>
          <p className="text-sm text-center mb-6 dark:text-gray-300">
            We've sent an OTP to:{" "}
            <span className="font-medium">{actualEmail}</span>
          </p>
          <form className="space-y-6">
            <div className="flex justify-center gap-5">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el!)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-12 h-12 text-center text-xl font-bold border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-dark-background dark:text-white"
                />
              ))}
            </div>
          </form>

          <div className="text-center mt-4">
            <p className="dark:text-gray-300">OTP will expires in 10 mins</p>
          </div>

          {/* The captcha has to sit on the screen that makes the request --
              resend calls the same protected route the first send does. */}
          <div className="flex justify-center items-center mt-5">
            <TurnstileWidget
              onSuccess={setCaptchaToken}
              onError={refreshCaptcha}
              onExpire={refreshCaptcha}
              resetSignal={captchaResetSignal}
            />
          </div>

          <div className="text-center mt-5">
            {/* {error && <p className="text-red-600 text-md mb-2 font-bold">{error}</p>} */}

            <button
              type="button"
              onClick={handleResendOTP}
              disabled={resendLoading || !captchaToken}
              className={`transition-colors font-medium underline ${
                resendLoading || !captchaToken
                  ? "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-700"
              }`}
            >
              {resendLoading ? "Sending..." : "Resend OTP"}
            </button>
          </div>

          <button
            onClick={handleVerify}
            className="bg-blue-600 hover:bg-blue-700 transition-colors text-white px-4 py-2 rounded w-full mt-5"
          >
            {condition === undefined
              ? "Verify & Login"
              : "Verify & Create Account"}
          </button>
        </div>
      </div>
    </>
  );
};

export default VerifyOtp;
